import { FC, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { KomplyanceAiWorkflowStartedChunk } from '@libs/types';
import { Box, IconButton, InputBase, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { queryKeys } from '@constants';
import { komplyanceAiApiService } from '@features/komplyance-ai';
import { useTrans } from '@libs/i18n';
import { ArrowRightIcon, PaperPlaneTiltIcon, ScalesIcon, SparkleIcon } from '@phosphor-icons/react';

import { DashboardWidget } from './DashboardWidget';

/**
 * Compact "Ask Athr AI" launcher: a question box plus quick prompts. Submitting
 * starts a conversation via the shared komplyance-ai service and navigates into
 * the chat (mirroring `NewChatPage`).
 */
export const AILaunchWidget: FC = observer(() => {
  const theme = useTheme();
  const { t } = useTrans();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');

  const prompts = [
    { icon: <ScalesIcon size={16} />, text: t('app.dashboard.ai.prompt1') },
    { icon: <ScalesIcon size={16} />, text: t('app.dashboard.ai.prompt2') },
  ];

  const submit = async (message: string) => {
    const trimmed = message.trim();
    if (!trimmed) return;
    await komplyanceAiApiService.sendMessage(
      { message: trimmed, deepResearch: false },
      (chunk: KomplyanceAiWorkflowStartedChunk) => {
        queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversations });
        navigate(`/chat/${chunk.conversation_id}`, {
          state: { initialMessage: trimmed, initialDeepResearchEnabled: false },
        });
      },
    );
  };

  return (
    <DashboardWidget
      icon={<SparkleIcon size={19} weight="fill" />}
      iconColor={theme.palette.blue[700]}
      title={t('app.dashboard.ai.title')}
      subtitle={t('app.dashboard.ai.subtitle')}
    >
      <Box sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            border: `1px solid ${theme.palette.grey[400]}`,
            borderRadius: 3,
            pl: 1.75,
            pr: 0.75,
            py: 0.75,
            backgroundColor: theme.palette.common.white,
          }}
        >
          <InputBase
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                submit(value);
              }
            }}
            placeholder={t('app.dashboard.ai.placeholder')}
            sx={{ flex: 1, fontSize: 13 }}
          />
          <IconButton
            onClick={() => submit(value)}
            disabled={!value.trim()}
            sx={{
              width: 34,
              height: 34,
              borderRadius: 2,
              backgroundColor: value.trim() ? theme.palette.text.primary : theme.palette.grey[300],
              color: value.trim() ? theme.palette.background.paper : theme.palette.text.tertiary,
              '&:hover': { backgroundColor: theme.palette.text.primary, opacity: 0.9 },
            }}
          >
            <PaperPlaneTiltIcon size={17} />
          </IconButton>
        </Box>

        <Typography
          sx={{ fontSize: 10.5, fontWeight: 600, color: 'text.tertiary', letterSpacing: '.05em', textTransform: 'uppercase' }}
        >
          {t('app.dashboard.ai.quickPrompts')}
        </Typography>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {prompts.map((p, i) => (
            <Box
              key={i}
              onClick={() => submit(p.text)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.375,
                border: `1px solid ${theme.palette.grey[300]}`,
                borderRadius: 3,
                p: 1.375,
                cursor: 'pointer',
                backgroundColor: theme.palette.common.white,
                '&:hover': { borderColor: theme.palette.grey[400] },
              }}
            >
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  color: theme.palette.violet[700],
                  backgroundColor: theme.palette.grey[100],
                }}
              >
                {p.icon}
              </Box>
              <Typography sx={{ flex: 1, fontSize: 12, lineHeight: 1.4 }}>{p.text}</Typography>
              <ArrowRightIcon size={13} color={theme.palette.text.tertiary} />
            </Box>
          ))}
        </Box>
      </Box>
    </DashboardWidget>
  );
});
